/** Landing page: totals and the leads worth opening first. */

import { useQuery } from '@tanstack/react-query';
import { graphql } from '../gql';
import { request } from '../lib/graphql';
import { DataTable } from '../components/DataTable';
import { Amount, ErrorState, Link, Loading, PageHead, SectionHead, Stat } from '../components/ui';
import { compact, num, truncate } from '../lib/format';

const OVERVIEW = graphql(`
  query Overview {
    # The same population the Фирми page lists: firms the public-funds
    # record names, not the owners and creditors the register brings in
    # above them.
    companiesAggregate(where: { public_funds: true }) { count }
    peopleAggregate { count }
    tendersAggregate { count value_bgn { sum } }
    subsidiesAggregate { count amount { sum } }
    municipalitiesAggregate { count }
    partiesAggregate { count }
    taxDebtRecordsAggregate { count }
    riskOverlap(limit: 8) { uic name total debt }
    topTenderWinners(limit: 10) { uic name count total }
    topSubsidyRecipients(limit: 10) { uic name count total }
    municipalityProcurement(limit: 10) {
      key name province population mayor party partyKey tenderCount tenderTotal
    }
  }
`);

export function OverviewPage() {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['overview'],
    queryFn: () => request(OVERVIEW),
  });

  // the page opens on a row of figures, so the wait is shaped like one
  if (isPending) return <Loading variant="cards" rows={7} />;
  if (isError) return <ErrorState message={(error).message} />;

  return (
    <>
      <PageHead
        eyebrow="Обзор"
        title="Кой получава публични средства"
        lede={'Обединени данни от обществените поръчки, европейските фондове, търговския '
          + 'регистър и изборните резултати. Всяка сума е такава, каквато е публикувана — '
          + 'нищо не е приписвано.'}
      />

      {/* Totals are shown compact — "1,2 млрд." rather than every digit.
          At this magnitude the digits are noise; the order of magnitude is
          the fact. */}
      <div className="grid grid-4">
        <Stat value={num(data.companiesAggregate.count)} label="Фирми" />
        <Stat value={num(data.tendersAggregate.count)} label="Поръчки" />
        <Stat value={compact(data.tendersAggregate.value_bgn?.sum)} label="Възложени (лв.)" />
        <Stat value={num(data.subsidiesAggregate.count)} label="Субсидии" />
        <Stat value={compact(data.subsidiesAggregate.amount?.sum)} label="Изплатени (лв.)" />
        <Stat value={num(data.peopleAggregate.count)} label="Лица" />
        <Stat value={num(data.municipalitiesAggregate.count)} label="Общини" />
        <Stat value={num(data.partiesAggregate.count)} label="Партии" />
      </div>

      {/* The flagship lead: companies with unpaid tax debt that are winning
          public money anyway. */}
      {data.riskOverlap.length > 0 && (
        <section className="block">
          <SectionHead title="Съвпадения за проверка"
            aside="задължения към НАП + публично финансиране" />
          <p className="page-lede" style={{ marginBottom: '1rem' }}>
            Фирми с непогасени данъчни задължения, които въпреки това печелят
            поръчки или получават субсидии.
          </p>
          <DataTable
            rows={data.riskOverlap}
            rowKey={(row) => row.uic}
            columns={[
              {
                key: 'name',
                label: 'Фирма',
                render: (row) => (
                  <Link href={`#/companies/${row.uic}`}>{truncate(row.name ?? row.uic, 46)}</Link>
                ),
              },
              {
                key: 'debt',
                label: 'Задължение',
                num: true,
                render: (row) => <Amount value={row.debt} className="debt" />,
              },
              {
                key: 'total',
                label: 'Публични средства',
                num: true,
                render: (row) => <Amount value={row.total} />,
              },
            ]}
          />
        </section>
      )}

      <section className="block">
        <SectionHead title="Най-често печелещи фирми"
          aside="всеки договор се брои веднъж, дори при няколко изпълнителя" />
        <DataTable
          rows={data.topTenderWinners}
          rowKey={(row) => row.uic}
          columns={[
            {
              key: 'name',
              label: 'Фирма',
              render: (row) => (
                <Link href={`#/companies/${row.uic}`}>{truncate(row.name ?? row.uic, 46)}</Link>
              ),
            },
            { key: 'count', label: 'Поръчки', num: true, render: (row) => num(row.count) },
            {
              key: 'total',
              label: 'Стойност',
              num: true,
              render: (row) => <Amount value={row.total} />,
            },
          ]}
        />
      </section>

      <section className="block">
        <SectionHead title="Общини по възложени поръчки" aside="кмет по данни на ЦИК" />
        <DataTable
          rows={data.municipalityProcurement}
          rowKey={(row) => row.key}
          columns={[
            {
              key: 'name',
              label: 'Община',
              render: (row) => (
                <Link href={`#/municipalities/${encodeURIComponent(row.key)}`}>{row.name}</Link>
              ),
            },
            { key: 'province', label: 'Област', render: (row) => row.province ?? '—' },
            { key: 'mayor', label: 'Кмет', render: (row) => truncate(row.mayor ?? '—', 26) },
            {
              key: 'party',
              label: 'Партия',
              render: (row) => (row.party
                ? (
                  <Link href={`#/parties/${encodeURIComponent(row.partyKey ?? row.party)}`}>
                    {truncate(row.party, 30)}
                  </Link>
                )
                : '—'),
            },
            { key: 'count', label: 'Поръчки', num: true, render: (row) => num(row.tenderCount) },
            {
              key: 'total',
              label: 'Стойност',
              num: true,
              render: (row) => <Amount value={row.tenderTotal} />,
            },
          ]}
        />
      </section>

      <section className="block">
        <SectionHead title="Най-големи получатели на субсидии" aside="ИСУН и ДФ „Земеделие“" />
        <DataTable
          rows={data.topSubsidyRecipients}
          rowKey={(row) => row.uic}
          columns={[
            {
              key: 'name',
              label: 'Фирма',
              render: (row) => (
                <Link href={`#/companies/${row.uic}`}>{truncate(row.name ?? row.uic, 46)}</Link>
              ),
            },
            { key: 'count', label: 'Субсидии', num: true, render: (row) => num(row.count) },
            {
              key: 'total',
              label: 'Сума',
              num: true,
              render: (row) => <Amount value={row.total} />,
            },
          ]}
        />
      </section>
    </>
  );
}
